import React from 'react';
import { ChangeTheme, useIsDarkTheme, useThemeColors } from './Theme';
import {
  SunIcon,
  MoonIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

export const ThemeTestPage: React.FC = () => {
  const isDark = useIsDarkTheme();
  const colors = useThemeColors();

  return (
    <div className="bg-base-100 min-h-screen p-8">
      <div className="mx-auto max-w-4xl space-y-8">
        {/* Header with Theme Switcher */}
        <div className="card-brand p-6">
          <div className="mb-4 flex items-center justify-between">
            <h1 className="text-brand-heading text-3xl">
              Theme Test - {isDark ? 'Dark' : 'Light'} Mode
            </h1>
            <ChangeTheme />
          </div>
          <p className="text-brand-body">
            This page demonstrates the new TBit branding theme. Use the theme
            switcher above to toggle between light and dark modes.
          </p>
        </div>

        {/* Color Palette */}
        <div className="card-brand p-6">
          <h2 className="text-brand-heading mb-4 text-xl">Brand Colors</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <div className="text-center">
              <div className="bg-brand-primary mb-2 h-16 w-full rounded-lg"></div>
              <p className="text-brand-caption text-xs">Primary</p>
            </div>
            <div className="text-center">
              <div className="bg-brand-secondary mb-2 h-16 w-full rounded-lg"></div>
              <p className="text-brand-caption text-xs">Secondary</p>
            </div>
            <div className="text-center">
              <div className="bg-brand-accent mb-2 h-16 w-full rounded-lg"></div>
              <p className="text-brand-caption text-xs">Accent</p>
            </div>
            <div className="text-center">
              <div className="bg-brand-coral mb-2 h-16 w-full rounded-lg"></div>
              <p className="text-brand-caption text-xs">Coral</p>
            </div>
            <div className="text-center">
              <div className="bg-brand-yellow mb-2 h-16 w-full rounded-lg"></div>
              <p className="text-brand-caption text-xs">Yellow</p>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="card-brand p-6">
          <h2 className="text-brand-heading mb-4 text-xl">Button Styles</h2>
          <div className="flex flex-wrap gap-4">
            <button className="btn btn-primary rounded-full">
              Primary Button
            </button>
            <button className="btn btn-secondary">Secondary Button</button>
            <button className="btn btn-primary rounded-full">
              Ghost Button
            </button>
            <button className="btn-brand-gradient">Gradient Button</button>
          </div>
        </div>

        {/* Alerts */}
        <div className="card-brand p-6">
          <h2 className="text-brand-heading mb-4 text-xl">Alert Components</h2>
          <div className="space-y-3">
            <div className="alert-brand-success flex items-center gap-3">
              <CheckCircleIcon className="h-5 w-5" />
              <span>Success: Your changes have been saved!</span>
            </div>
            <div className="alert-brand-warning flex items-center gap-3">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <span>Warning: Please review your input.</span>
            </div>
            <div className="alert-brand-error flex items-center gap-3">
              <XCircleIcon className="h-5 w-5" />
              <span>Error: Something went wrong.</span>
            </div>
          </div>
        </div>

        {/* Form Elements */}
        <div className="card-brand p-6">
          <h2 className="text-brand-heading mb-4 text-xl">Form Elements</h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="label">
                <span className="label-text font-poppins">Input Field</span>
              </label>
              <input
                type="text"
                className="input-brand w-full"
                placeholder="Type something..."
              />
            </div>
            <div>
              <label className="label">
                <span className="label-text font-poppins">Select Dropdown</span>
              </label>
              <select className="select-bordered font-poppins select w-full">
                <option>Option 1</option>
                <option>Option 2</option>
                <option>Option 3</option>
              </select>
            </div>
          </div>
        </div>

        {/* Gradient Examples */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="bg-brand-gradient card p-6 text-white">
            <h3 className="font-poppins mb-3 text-lg font-bold">
              Primary Gradient
            </h3>
            <p className="font-poppins opacity-90">
              This card uses the primary brand gradient background.
            </p>
            <button className="btn btn-outline mt-4 border-white text-white hover:bg-white hover:text-gray-900">
              Learn More
            </button>
          </div>

          <div className="bg-brand-gradient-coral card p-6 text-white">
            <h3 className="font-poppins mb-3 text-lg font-bold">
              Coral Gradient
            </h3>
            <p className="font-poppins opacity-90">
              This card uses the coral-lavender gradient background.
            </p>
            <button className="btn btn-outline mt-4 border-white text-white hover:bg-white hover:text-gray-900">
              Explore
            </button>
          </div>
        </div>

        {/* Current Theme Info */}
        <div className="card-brand p-6">
          <h2 className="text-brand-heading mb-4 text-xl">
            Current Theme Information
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <h3 className="text-brand-heading mb-2 text-lg">Theme State</h3>
              <ul className="text-brand-body space-y-1">
                <li>
                  Mode: <strong>{isDark ? 'Dark' : 'Light'}</strong>
                </li>
                <li>
                  Primary Color: <strong>{colors.primary}</strong>
                </li>
                <li>
                  Secondary Color: <strong>{colors.secondary}</strong>
                </li>
                <li>
                  Background: <strong>{colors.background}</strong>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-brand-heading mb-2 text-lg">Features</h3>
              <ul className="text-brand-body space-y-1">
                <li>✅ Poppins Font Family</li>
                <li>✅ Theme-aware Colors</li>
                <li>✅ Responsive Design</li>
                <li>✅ Accessibility Support</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
